import { NgClass } from '@angular/common';
import type { OnInit } from '@angular/core';
import { afterNextRender, Component, DestroyRef, inject, Injector, input, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { AbstractWebsiteComponent } from 'components/molecules/abstract-website-component/abstract-website-component';
import type { MenuItem } from 'components/organisms/sidebar/entities/types';
import { SidebarService } from 'components/organisms/sidebar/sidebar.service';
import { LinkTargetDetectorDirective } from 'src/directives/link-target-detector/link-target-detector.directive';
import { AnchorsService } from 'src/services/anchors/anchors.service';
import { LocaleService } from 'src/services/locale/locale.service';
import { UrlService } from 'src/services/url/url.service';

@Component({
    selector: 'website-menu-item',
    templateUrl: './menu-item.component.html',
    styleUrls: ['./menu-item.component.scss'],
    imports: [NgClass, LinkTargetDetectorDirective],
})
export class MenuItemComponent extends AbstractWebsiteComponent implements OnInit {
    public readonly COMPONENT_NAME = 'menu-item';

    public readonly injector = inject(Injector);
    private readonly sidebarService = inject(SidebarService);
    private readonly localeService = inject(LocaleService);
    private readonly urlService = inject(UrlService);
    public readonly anchorsService = inject(AnchorsService);
    private readonly destroyRef = inject(DestroyRef);

    readonly isFAQItem = input(false);
    readonly item = input.required<MenuItem>();
    readonly parentItem = input<MenuItem>();
    readonly level = input(1);

    public hasChildren: boolean;
    public isOpen = signal(false);
    public isActiveAnchor = signal(false);
    public url: string = null;
    public isCurrentMenu: boolean;

    ngOnInit() {
        const item = this.item();

        this.hasChildren = Boolean(item.children?.length);
        this.isCurrentMenu = item.url === this.urlService.getSecondUrlSegment();

        if (this.isCurrentMenu) {
            this.isOpen.set(true);
        }

        this.initLinkUrl();

        this.anchorsService.fragment$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((fragment) => {
            const currentItem = this.item();

            if (currentItem.isAnchor) {
                this.isActiveAnchor.set(currentItem.url === fragment);
            }
        });
    }

    public toggleOpen() {
        this.isOpen.set(!this.isOpen());
    }

    public anchorClick(url: string): void {
        this.sidebarService.hideMobileMenu();

        /** afterNextRender and requestAnimationFrame are needed to make sure
         * the scroll happens after the mobile menu is hidden and its height is updated */

        afterNextRender(
            () => {
                requestAnimationFrame(() => {
                    this.anchorsService.clickOnAnchor(url);
                });
            },
            { injector: this.injector }
        );
    }

    private initLinkUrl() {
        const item = this.item();
        const locale = this.localeService.getPathToLocaleSlug();
        const firstUrlSegment = this.urlService.getFirstUrlSegment();
        const pathname = `${locale}/${firstUrlSegment}`;
        const isSubitemAnchorLink = this.level() > 1 && !item.isAnchor;

        if (isSubitemAnchorLink && !this.isFAQItem()) {
            const anchorPart = `${this.parentItem()?.url}/#${item.url}`;

            this.url = `${pathname}/${anchorPart}`;
        } else {
            this.url = `${pathname}/${item.url ? item.url + '/' : ''}`;
        }
    }
}
